/**
 * RoofMapper Component - Public Types
 * Portable Solar Analysis Module for NIKO Energy
 */

import { BuildingInsights, DataLayers, LatLng } from '../../types/solar';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export type RoofMapperMode = 'fullscreen' | 'embedded' | 'modal' | 'sidebar';

export interface RoofMapperProps {
  // Layout Configuration
  mode?: RoofMapperMode;
  width?: string | number;
  height?: string | number;

  // Feature Toggles
  features?: Partial<RoofMapperFeatures>;

  // Initial State
  initialLocation?: LocationData;
  initialMapMode?: '2D' | '3D';

  // Event Callbacks
  onLocationSelect?: (location: LocationData) => void;
  onSolarDataFetched?: (data: SolarData) => void;
  onManualMeasurement?: (measurement: MeasurementData) => void;
  onSave?: (data: SaveData) => Promise<void>;
  onError?: (error: Error) => void;
  onMapReady?: () => void;

  // Data Integration
  rpu?: string;
  projectId?: string;
  customData?: Record<string, any>;

  // Styling & Theming
  theme?: Partial<RoofMapperTheme>;
  className?: string;
  style?: React.CSSProperties;

  // API Configuration (optional, uses provider defaults)
  apiEndpoint?: string;
  googleMapsApiKey?: string;
}

export interface RoofMapperFeatures {
  search: boolean;
  solarAnalysis: boolean;
  manualMeasurement: boolean;
  layerControls: boolean;
  export: boolean;
  history: boolean;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface RoofMapperConfig {
  // API Configuration
  apiEndpoint: string;
  googleMapsApiKey: string;

  // Authentication
  getAuthToken?: () => string | Promise<string>;

  // Default Settings
  defaultMapMode?: '2D' | '3D';
  defaultZoom?: number;
  defaultCenter?: LatLng;

  // Feature Flags
  enabledFeatures?: Partial<RoofMapperFeatures>;

  // Theming
  theme?: RoofMapperTheme;

  // Localization
  locale?: 'en' | 'es';
  translations?: Record<string, string>;
}

// ============================================================================
// THEMING
// ============================================================================

export interface RoofMapperTheme {
  colors: {
    primary: string;
    text: string;
    background: string;
    border: string;
    success: string;
    warning: string;
    error: string;
    info: string;
  };

  fonts: {
    primary: string;
    mono: string;
  };

  spacing: {
    unit: number;
    sidebar: number;
    navbar: number;
  };

  shadows: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };

  borderRadius: {
    sm: string;
    md: string;
    lg: string;
  };
}

// ============================================================================
// DATA TYPES
// ============================================================================

export interface LocationData {
  lat: number;
  lng: number;
  rpu?: string;
  address?: string;
  timestamp: Date;
}

export interface SolarData {
  maxPanels: number;
  roofArea: number;
  sunshineHours: number;
  segments: RoofSegment[];
  buildingInsights: BuildingInsights;
  dataLayers?: DataLayers;
}

export interface RoofSegment {
  index: number;
  panelCount: number;
  area: number;
  azimuth: number;
  pitch: number;
  coordinates: LatLng[];
}

export interface MeasurementData {
  type: 'solar' | 'manual';
  area: number;
  panelCount: number;
  systemSize?: number;
  coordinates: LatLng[];
  timestamp: Date;
}

export interface SaveData {
  location: LocationData;
  solarData?: SolarData;
  measurement?: MeasurementData;
  customData?: Record<string, any>;
}

// ============================================================================
// LAYER TYPES
// ============================================================================

export interface MapLayers {
  rgb: boolean;
  flux: boolean;
  dsm: boolean;
}

// ============================================================================
// DEFAULT THEME
// ============================================================================

export const defaultTheme: RoofMapperTheme = {
  colors: {
    primary: '#EBFD03',
    text: '#000000',
    background: '#FFFFFF',
    border: '#E5E7EB',
    success: '#00c853',
    warning: '#ffd600',
    error: '#ff3d00',
    info: '#3b82f6',
  },
  fonts: {
    primary: "'Inter', system-ui, sans-serif",
    mono: "'Courier New', monospace",
  },
  spacing: {
    unit: 4,
    sidebar: 285,
    navbar: 80,
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
  },
};

// ============================================================================
// DEFAULT FEATURES
// ============================================================================

export const defaultFeatures: RoofMapperFeatures = {
  search: true,
  solarAnalysis: true,
  manualMeasurement: false,
  layerControls: true,
  export: true,
  history: false,
};
